import { Button, Col, Row, Space, Table, TablePaginationConfig  } from "antd";
import { useContext, useEffect, useState } from "react";
import { ConfiguratorContext } from "../../context";
import { FilterValue, SortOrder, SorterResult } from "antd/lib/table/interface";
import Title from "antd/lib/typography/Title";
import { ArrayParam, NumberParam, StringParam, useQueryParam } from "use-query-params";
import { EntityType, PAGINATION_MAX_PAGE_SIZE, TruckDateFilterType, TruckFilter } from "../../api/models";
import Utils, {ExportableColumn} from "../../util/util";
import dayjs from "dayjs";
import useTruckReport from "../../swr/useTruckReport";
import TruckReportFilterControls from "../../components/Filter/TruckReportFilterControls";

type  TruckReportSort = SorterResult<TruckReportDto> | SorterResult<TruckReportDto>[]

export interface TruckReportDto {
  id: number;
  serialNumber?: string
  engineSerialNumber?: string
  transmissionSerialNumber?: string
  partNumber?: string
  customer?: string
  dealer?: string
  salesPerson?: string
  bmSalesReps?: string[]
  estProductionDate? : Date
  productionDate? : Date
  shipDate? : Date
  readyToShip? : Date
  totalPrice?: number
  entityType?: EntityType
  shippingDestination?: string
  steerAxle1?: string
  steerAxle2?: string
  steerAxle3?: string
  pusherAxle1?: string
  driveAxle1?: string
  driveAxle2?: string
  driveAxle3?: string
  tagAxle?: string
  vin?: string
  scr?: string
  dpf?: string
  purchaseOrder?: string
}


const TruckReport = () => {
  const configurator = useContext(ConfiguratorContext);
  const [searchFilterParam, setSearchFilterParam] = useQueryParam<string|undefined|null>("filter", StringParam);
  const [pageSizeQueryParam, setPageSizeQueryParam] = useQueryParam<number|undefined|null>("nr", NumberParam);
  const [currentPageParam, setCurrentPageParam] = useQueryParam<number|undefined|null>("p", NumberParam);
  const [sortFieldQueryParam, setSortFieldQueryParam] = useQueryParam<string|undefined|null>("sf", StringParam);
  const [sortDirectionQueryParam, setSortDirectionQueryParam] = useQueryParam<string|undefined|null>("sd", StringParam);
  const [dealerFilterParam, setDealerFilterParam] = useQueryParam<any>("dealer", ArrayParam);
  const [entityTypeFilterParam, setEntityTypeFilterParam] = useQueryParam<any>("entityTypeList", ArrayParam);
  const [dateFilterTypeParam, setDateFilterTypeParam] = useQueryParam<string|undefined|null>("dateFilterType", StringParam);
  const [dateFilterStartParam, setDateFilterStartParam] = useQueryParam<string|undefined|null>("dateFilterStart", StringParam);
  const [dateFilterEndParam, setDateFilterEndParam] = useQueryParam<string|undefined|null>("dateFilterEnd", StringParam);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [filter, setFilter] = useState<TruckFilter>({
    search: searchFilterParam || undefined,
    entityTypeList: [entityTypeFilterParam as (string|null) || []].flat(),
    dealerLst: dealerFilterParam || undefined, //silly fix for null
    dateFilterType: dateFilterTypeParam as TruckDateFilterType,
    dateFilterStart: dateFilterStartParam ? dayjs(dateFilterStartParam) : undefined,
    dateFilterEnd: dateFilterStartParam ? dayjs(dateFilterEndParam) : undefined,
  });

  const defaultSort = {
    columnKey: 'serialNumber',
    order: 'descend' as SortOrder
  };
  const [sort, setSort] = useState<TruckReportSort>({
    columnKey: sortFieldQueryParam || defaultSort.columnKey,
    order: sortDirectionQueryParam as ( SortOrder | undefined ) || defaultSort.order
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    total: 0,
    position: ["topLeft", "bottomLeft"],
    pageSize: pageSizeQueryParam == null || pageSizeQueryParam > 500 ? 20 : pageSizeQueryParam,
    current: currentPageParam == null || currentPageParam < 1 ? 1 : currentPageParam,
  });

  useEffect(() => {
    setPageSizeQueryParam(pagination.pageSize);
    setCurrentPageParam(pagination.current);
  }, [pagination.pageSize, pagination.current ]);


  const truckReportAsync = useTruckReport({
    ...filter,
    dateFilterStart: filter.dateFilterStart?.toDate(),
    dateFilterEnd: filter.dateFilterEnd?.toDate(),
    page: ( pagination.current || 1 ) - 1,
    size: pagination.pageSize || 20,
    sort: {
      field: sort[0]?.columnKey?.toString() || defaultSort.columnKey,
      direction: sort[0]?.order === 'descend' ? 'desc' : 'asc',
    }
  })


  const tableOnChange =  (pagination:TablePaginationConfig, _filters:Record<string, FilterValue | null>, sorter: SorterResult<TruckReportDto> | SorterResult<TruckReportDto>[]) => {
    setPagination(pagination);

    const firstSort = [ sorter ].flatMap(v => v)[0];
    setSortFieldQueryParam( firstSort.columnKey?.toString() );
    setSortDirectionQueryParam( firstSort.order );
    // setSort(sorter);
  };

  const getFilterWithDateRange = (filter: TruckFilter) => {
    return filter['dateRange'] ? {...filter, dateFilterStart: filter['dateRange'][0], dateFilterEnd: filter['dateRange'][1]} : filter;
  }

  const onFilterChange = (_values: Record<string, any>, filter: TruckFilter ) => {
    filter = getFilterWithDateRange(filter);
    setSearchFilterParam(filter.search);
    setPagination({ ...pagination, current: 1 });
    setEntityTypeFilterParam(filter.entityTypeList);
    setDealerFilterParam( filter.dealerLst );
    filter.dateFilterType && setDateFilterTypeParam(filter.dateFilterType);
    filter['dateRange'] && setDateFilterStartParam((filter['dateRange']?.[0]).toString());
    filter['dateRange'] && setDateFilterEndParam((filter['dateRange']?.[1]).toString());
    setFilter(filter);
  };

  const resetQueryParam = () => {
    !!searchFilterParam && setSearchFilterParam(undefined);
    !!dealerFilterParam?.length && setDealerFilterParam( undefined );
    !!dateFilterTypeParam && setDateFilterTypeParam(undefined);
    !!dateFilterStartParam && setDateFilterStartParam(undefined);
    !!dateFilterEndParam && setDateFilterEndParam(undefined);
    !!entityTypeFilterParam && setEntityTypeFilterParam(undefined);
  }


  const handleExportTrucks = async () => {
    setIsExporting(true);
    const resp = await configurator.api.fetchTruckReport({
      ...filter,
      dateFilterStart: filter.dateFilterStart?.toDate(),
      dateFilterEnd: filter.dateFilterEnd?.toDate(),
      page: 0,
      size: PAGINATION_MAX_PAGE_SIZE,
      sort: {
        field: [sort].flat()[0].columnKey?.toString() || defaultSort.columnKey,
        direction: [sort].flat()[0].order === 'descend' ? 'desc' : 'asc',
      }
    })

    const trucks = resp.data.content;
    if ( !trucks.length ) return;

    const csvFileNameComponents = [ "trucks", Utils.buildSerialNumberStr(trucks.map(t=> t.serialNumber + "")) ];

    const csvFileName = csvFileNameComponents.join( "_" )

    Utils.exportDataAsCSV(csvFileName, trucks, columns);
    setIsExporting(false);
  };


  const columns:ExportableColumn<TruckReportDto>[] = [
    {
      title: "Serial Number",
      key: "truck.serialNumber",
      render: (t) => t.serialNumber,
      renderCSV: (t) => String(t.serialNumber),
    },
    {
      title: "VIN",
      key: "vin",
      render: (t) => t.vin,
      renderCSV: (t) => t.vin,
    },
    {
      title: "Engine Serial Number",
      render: (t) => t.engineSerialNumber,
      renderCSV: (t) => t.engineSerialNumber,
    },
    {
      title: "Transmission Serial Number",
      render: (t) => t.transmissionSerialNumber,
      renderCSV: (t) => t.transmissionSerialNumber,
    },
    {
      title : 'Steer Axle Sn',
      key: "steerAxle",
      render: (t) => [t.steerAxle1, t.steerAxle2, t.steerAxle3].filter(v=>v).join(","),
      renderCSV: (t) => [t.steerAxle1, t.steerAxle2, t.steerAxle3].filter(v=>v).join(",")
    },
    {
      title : 'Drive Axle Sn',
      key: "driveAxle",
      render: (t) => [t.driveAxle1, t.driveAxle2, t.driveAxle3].filter(v=>v).join(","),
      renderCSV: (t) => [t.driveAxle1, t.driveAxle2, t.driveAxle3].filter(v=>v).join(",")
    },
    {
      title : 'Pusher Axle Sn',
      key: "pusherAxle",
      render: (t) => t.pusherAxle1,
      renderCSV: (t) => t.pusherAxle1
    },
    {
      title : 'Tag Axle Sn',
      key: "tagAxle",
      render: (t) => t.tagAxle,
      renderCSV: (t) => t.tagAxle
    },
    {
      title: "CARB Serial",
      key: "scr",
      render: (t) => t.scr,
      renderCSV: (t) => t.scr,
    },
    {
      title: "DPF Serial",
      key: "dpf",
      render: (t) => t.dpf,
      renderCSV: (t) => t.dpf,
    },
    {
      title: "Purchase Order",
      key: "purchaseOrder",
      render: (t) => t.purchaseOrder,
      renderCSV: (t) => t.purchaseOrder,
    },
    {
      title: "Part Number",
      key: "partNumber",
      render: (t) => t.partNumber,
      renderCSV: (t) => t.partNumber,
    },
    {
      title: "Customer",
      key: "customer",
      render: (t) => t.customer,
      renderCSV: (t) => t.customer,
    },
    {
      title: "Entity Type",
      key: "entityType",
      render: (t) => t.entityType,
      renderCSV: (t) => t.entityType,
    },
    {
      title: "Destination",
      dataIndex: "shippingDestination",
      renderCSV: (t) => t.shippingDestination
    },
    {
      title: "Dealer",
      dataIndex: "dealer",
      renderCSV: (t) => t.dealer
    },
    {
      title: "Sales Person",
      dataIndex: "salesPerson",
      renderCSV: (t) => t.salesPerson
    },
    {
      title: "BM Sales Reps",
      dataIndex: "bmSalesReps",
      render: (reps) => reps?.join(", "),
      renderCSV: (t) => t.bmSalesReps?.join(", "),
    },
    {
      title: "Total Price",
      key: "totalPrice",
      render: (t) => Utils.formatMoney( t.totalPrice ),
      renderCSV: (t) => Utils.formatMoney( t.totalPrice ),
    },
    {
      title: "Production date",
      key: "productionDate",
      render: (t) =>
          t?.productionDate ? dayjs(t?.productionDate).format("MM/DD/YYYY") : "",
      renderCSV: (t) =>
          t?.productionDate ? dayjs(t?.productionDate).format("MM/DD/YYYY") : "",
    },
    {
      title: "Ship Date",
      key: "shipDate",
      render: (t) =>
          t?.shipDate ? dayjs(t?.shipDate).format("MM/DD/YYYY") : "",
      renderCSV: (t) =>
          t?.shipDate ? dayjs(t?.shipDate).format("MM/DD/YYYY") : ""
    },
    {
      title: "Ready To Ship",
      key: "readyToShip",
      render: (t) =>
          t?.readyToShip ? dayjs(t?.readyToShip).format("MM/DD/YYYY") : "",
      renderCSV: (t) =>
          t?.shipDate ? dayjs(t?.shipDate).format("MM/DD/YYYY") : ""
    },
  ];

  const newButtonStyle:any = pagination.total
      ? { position: "absolute", top: "1rem", zIndex: 1, right: "0px" }
      : { float: "right", marginBottom: "1rem" };


  return (
    <div className="site-layout-background">
      <Title level={2}>Truck Report</Title>
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <TruckReportFilterControls
          filter={filter}
          onFilterChange={onFilterChange}
          resetQueryParam={resetQueryParam}
        />
        <div style={{ position: "relative" }}>
          <Row gutter={12} justify={"end"} style={newButtonStyle}>
            <Col>
              <Button type="primary" loading={isExporting} onClick={handleExportTrucks}>Export</Button>
            </Col>
          </Row>
          <Table
              loading={truckReportAsync.isLoading}
              onChange={tableOnChange}
              bordered
              pagination={{...pagination, total: truckReportAsync.data?.totalElements}}
              dataSource={truckReportAsync.data?.content}
              columns={columns}
              rowKey={(t) => t.serialNumber + "-" + t.partNumber}
              scroll={{ x: "120%" }}
          />
        </div>
      </Space>
    </div>
  );
};

export default TruckReport;


